// See all's search, filters and day groups (components/NewRolesCard.tsx).
// Each role comes with `level`, `targets` and `places` worked out for the
// user by the backend (new_roles.role_facets), so the app only compares.

import type { FeedRole } from "./newRoles";

export type FilterKey = "level" | "target" | "place" | "show";
/** The options picked in each group. A role passes a group when it has any of them; a group with none picked passes all. */
export type RoleFilters = Record<FilterKey, string[]>;
export const NO_FILTERS: RoleFilters = { level: [], target: [], place: [], show: [] };

export type FilterGroup = { key: FilterKey; label: string; options: { value: string; label: string }[] };

/** Whether the backend sent level / targets / places (it didn't before role_facets, nor in an older saved copy) */
export const hasFacets = (roles: FeedRole[]) => roles.some((role) => role.targets !== undefined);

/** Level, the user's roles, their places, and Show only. Without `facets`, Show only alone. */
export function filterGroups(targets: string[], places: string[], facets = true): FilterGroup[] {
  const list = (values: string[]) => values.map((value) => ({ value, label: value }));
  return [
    {
      key: "level",
      label: "Level",
      options: [
        { value: "intern", label: "Internship" },
        { value: "new_grad", label: "New grad" },
        { value: "none", label: "No level listed" },
      ],
    },
    { key: "target", label: "Role", options: list(targets) },
    { key: "place", label: "Location", options: list(places) },
    {
      key: "show",
      label: "Show only",
      options: [
        { value: "dream", label: "Dream companies" },
        { value: "saved", label: "Saved" },
      ],
    },
  ].filter((group) => group.options.length > 0 && (facets || group.key === "show")) as FilterGroup[];
}

function hasOption(role: FeedRole, key: FilterKey, value: string, saved: Set<string>): boolean {
  switch (key) {
    case "level":
      return value === "none" ? !role.level : role.level === value;
    case "target":
      return !!role.targets?.includes(value);
    case "place":
      return !!role.places?.includes(value);
    case "show":
      return value === "dream" ? !!role.dream : saved.has(role.id);
  }
}

/** Every group passes; `skip` leaves one out (an option's count is with the others applied) */
function passes(role: FeedRole, filters: RoleFilters, saved: Set<string>, skip?: FilterKey): boolean {
  return (Object.keys(filters) as FilterKey[]).every((key) => {
    if (key === skip || filters[key].length === 0) return true;
    // Show only: each picked one must hold ("Dream companies" and "Saved": saved at a dream company)
    if (key === "show") return filters.show.every((value) => hasOption(role, key, value, saved));
    return filters[key].some((value) => hasOption(role, key, value, saved));
  });
}

const fold = (text: string) =>
  text.normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Every word of the search is in the title or company ("stripe intern", "montréal") */
export function matchesSearch(role: FeedRole, query: string): boolean {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const text = fold(`${role.title} ${role.company}`);
  return words.every((w) => text.includes(w));
}

export function filterRoles(roles: FeedRole[], query: string, filters: RoleFilters, saved: Set<string>): FeedRole[] {
  return roles.filter((role) => matchesSearch(role, query) && passes(role, filters, saved));
}

/** How many roles picking this option would show, with the search and the other groups applied */
export function optionCount(
  roles: FeedRole[],
  query: string,
  filters: RoleFilters,
  saved: Set<string>,
  key: FilterKey,
  value: string,
): number {
  const others = key === "show" ? { ...filters, show: filters.show.filter((v) => v !== value) } : filters;
  return roles.filter(
    (role) =>
      matchesSearch(role, query) &&
      passes(role, others, saved, key === "show" ? undefined : key) &&
      hasOption(role, key, value, saved),
  ).length;
}

export const activeCount = (filters: RoleFilters) =>
  (Object.keys(filters) as FilterKey[]).reduce((n, key) => n + filters[key].length, 0);

export function toggleFilter(filters: RoleFilters, key: FilterKey, value: string): RoleFilters {
  const on = filters[key].includes(value);
  return { ...filters, [key]: on ? filters[key].filter((v) => v !== value) : [...filters[key], value] };
}

// --- Day groups --------------------------------------------------------------------

const DAY_MS = 24 * 3600000;

/**
 * Roles under Today / Yesterday / Earlier this week / Older, each group in
 * the order given. Empty groups left out. The same spans as the tabs
 * (newRoles.ROLE_WINDOWS): Today is the last 24 hours, so its count matches
 * the Today tab.
 */
export function dayGroups(roles: FeedRole[], now = Date.now()): { label: string; roles: FeedRole[] }[] {
  const groups = [
    { label: "Today", from: now - DAY_MS },
    { label: "Yesterday", from: now - 2 * DAY_MS },
    { label: "Earlier this week", from: now - 7 * DAY_MS },
    { label: "Older", from: -Infinity },
  ].map((g) => ({ ...g, roles: [] as FeedRole[] }));
  for (const role of roles) {
    const t = Date.parse(role.listedAt) || 0;
    groups.find((g) => t >= g.from)!.roles.push(role);
  }
  return groups.filter((g) => g.roles.length > 0).map(({ label, roles }) => ({ label, roles }));
}
